"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";
import React, { useEffect, useState } from "react";
import GalleryView from "./GalleryView";
import ListingViews from "@/components/listing/ListingViews";
import type { ListingViewStats } from "@/lib/types/search";
import { DEFAULT_IMAGE } from "@/lib/data/images";
import { IMAGE_SIZES } from "@/lib/imageSizes";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { ImageOff, Images } from "lucide-react";
import { twMerge } from "tailwind-merge";

type Props = {
  images: Array<string>;
  views: ListingViewStats | null;
};

const ListingGallery: React.FC<Props> = ({ images, views }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const t = useTranslations("listing");

  const onOpenGallery = (index = 0) => {
    if (images.length < 2) return;
    setCurrentIndex(index);
    setIsDialogOpen(true);
  };

  const count = images.length;
  // Desktop layout adapts to how many photos exist: 1 → hero only,
  // 2 → hero + one tall thumb, 3-4 → hero + two stacked thumbs,
  // 5+ → hero + four thumbs (two visible below lg).
  const thumbs = images.slice(1, count >= 5 ? 5 : 3);

  // No photos at all: show a clear notice instead of a giant placeholder hero.
  if (count === 0) {
    return (
      <div className="flex flex-row items-center gap-3 rounded-2xl border border-orange-200 bg-orange-50 px-4 py-4 text-sm font-medium text-orange-700">
        <ImageOff className="size-5 min-w-5" />
        <p>{t("no-photos-info")}</p>
      </div>
    );
  }

  return (
    <>
      <GalleryView
        currentIndex={currentIndex}
        setCurrentIndex={setCurrentIndex}
        imageUrls={images}
        isOpen={isDialogOpen}
        setIsOpen={setIsDialogOpen}
      />

      {/* Mobile: swipeable carousel */}
      <div className="relative sm:hidden">
        {count > 1 ? (
          <MobileCarousel
            images={images}
            onOpen={onOpenGallery}
            alt={(index) => t("image-alt", { number: index + 1, total: count })}
          />
        ) : (
          <Image
            src={count > 0 ? images[0] : DEFAULT_IMAGE.LISTING}
            alt={t("image-alt", { number: 1, total: count })}
            width={800}
            priority
            height={600}
            unoptimized
            sizes={IMAGE_SIZES.hero}
            className="aspect-[4/3] w-full rounded-2xl border border-input object-cover"
          />
        )}
        {views && <ListingViews views={views} variant="gallery" />}
      </div>

      {/* Tablet & desktop: hero + thumbnail grid */}
      <div
        className={twMerge(
          "relative hidden gap-2 sm:grid",
          thumbs.length === 0 && "grid-cols-1",
          thumbs.length > 0 &&
            "h-[380px] grid-cols-[2fr_1fr] grid-rows-2 lg:h-[460px]",
          thumbs.length > 2 && "lg:grid-cols-[2fr_1fr_1fr]",
        )}
      >
        <div
          className={twMerge(
            "relative min-h-0 overflow-hidden rounded-2xl",
            thumbs.length > 0 && "row-span-2",
          )}
        >
          <Image
            src={count > 0 ? images[0] : DEFAULT_IMAGE.LISTING}
            alt={t("image-alt", { number: 1, total: count })}
            width={1200}
            // Deliberately NOT `priority`: this grid is `hidden sm:grid`, but
            // `priority` emits a <link rel=preload> regardless of CSS visibility,
            // so phones were preloading this 1200x800 hero on top of the mobile
            // one. `eager` keeps it off the lazy queue for real desktop viewers
            // without forcing a preload on mobile.
            loading="eager"
            height={800}
            unoptimized
            sizes={IMAGE_SIZES.hero}
            onClick={() => onOpenGallery(0)}
            className={twMerge(
              "h-full w-full rounded-2xl border border-input object-cover",
              thumbs.length === 0 ? "max-h-[420px]" : "cursor-pointer",
            )}
          />
          {views && <ListingViews views={views} variant="gallery" />}
        </div>
        {thumbs.map((image, index) => (
          <Image
            key={`thumb-${index}`}
            src={image}
            alt={t("image-alt", { number: index + 2, total: count })}
            width={600}
            height={400}
            unoptimized
            sizes={IMAGE_SIZES.thumb200}
            onClick={() => onOpenGallery(index + 1)}
            className={twMerge(
              "h-full w-full cursor-pointer rounded-2xl border border-input object-cover",
              // With one extra photo it fills the whole right column.
              thumbs.length === 1 && "row-span-2",
              // Thumbs 3 & 4 only fit the wide 3-column layout.
              index >= 2 && "hidden lg:block",
            )}
          />
        ))}
        {count > 1 && (
          <button
            onClick={() => onOpenGallery(0)}
            className="absolute bottom-3 right-3 flex flex-row items-center gap-2 rounded-full border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground shadow-sm transition-colors hover:bg-muted"
          >
            <Images className="size-4" />
            <span>{t("see-photos", { count })}</span>
          </button>
        )}
      </div>
    </>
  );
};

export default ListingGallery;

type MobileCarouselProps = {
  images: Array<string>;
  onOpen: (index: number) => void;
  alt: (index: number) => string;
};

const MobileCarousel: React.FC<MobileCarouselProps> = ({
  images,
  onOpen,
  alt,
}) => {
  const [api, setApi] = useState<CarouselApi>();
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    if (!api) return;
    const onSelect = () => setSelected(api.selectedScrollSnap());
    api.on("select", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  return (
    <div className="relative">
      <Carousel setApi={setApi} opts={{ loop: true }}>
        <CarouselContent>
          {images.map((image, index) => (
            <CarouselItem key={`mobile-${index}`}>
              <Image
                src={image}
                alt={alt(index)}
                width={800}
                height={600}
                priority={index === 0}
                unoptimized
                sizes={IMAGE_SIZES.hero}
                onClick={() => onOpen(index)}
                className="aspect-[4/3] w-full rounded-2xl border border-input object-cover"
              />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
      <span className="pointer-events-none absolute bottom-3 right-3 flex flex-row items-center gap-1.5 rounded-full bg-foreground/75 px-2.5 py-1 text-xs font-semibold text-background">
        <Images className="size-3.5" />
        {selected + 1} / {images.length}
      </span>
    </div>
  );
};
